import {
  AuthCredential,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPhoneNumber,
  linkWithPopup,
  signInWithPhoneNumber,
  signInWithPopup,
  OAuthProvider,
  onIdTokenChanged,
  PhoneAuthCredential,
  PhoneAuthProvider,
  RecaptchaVerifier,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  updatePhoneNumber
} from 'firebase/auth'
import type { ConfirmationResult, User } from 'firebase/auth'

import * as Sentry from '@sentry/vue'
import type { CookieRef } from 'nuxt/app'
import { useStore } from '~/stores/main'
import { changeRoute } from '~/composables/navigation'
import {
  getDefaultVehicle,
  getBasketContent,
  refreshUserToken,
  getShippingAddressList,
  getUserVat,
  getPaymentMethods,
  getChargingPreferences,
  mergeAnonymousBasket,
  getUserDetails,
  changeUserDetails,
  throwError
} from '~/composables/useFunctions'
import type { MiioUser } from '~/types/auth'
import { UserCookie } from '~/types/auth'
import { oneSignalService } from '~/composables/oneSignal'
import { FormTabEnum } from '~/types/enums'

let runStateChange = true
let useLoading = false

export function checkUserStatus(): void {
  const auth = getAuth()
  onIdTokenChanged(auth, async (currentUser): Promise<void> => {
    await validateCurrentUser(currentUser)
  })
}

export async function validateCurrentUser(currentUser: User | null) {
  const store = useStore()
  const app = useNuxtApp()
  const userCookie: CookieRef<UserCookie | null> = useCookie('hasUser')
  const refreshToken = useCookie('refreshToken')

  if (runStateChange) {
    if (useLoading) {
      store.loading = true
    }
    if (currentUser) {
      const isAnonymous = currentUser.isAnonymous
      userCookie.value = isAnonymous ? UserCookie.AnonymousUser : UserCookie.MiioUser
      store.firebaseUser = currentUser
      Sentry.setUser({ id: currentUser.uid, email: currentUser.email?.toString() })
      await refreshUserToken()
      refreshToken.value = currentUser.refreshToken
      oneSignalService.login(currentUser.uid)
      oneSignalService.addTag('is_anonymous', currentUser.isAnonymous ? '1' : '0')
      oneSignalService.addTag('last_used_platform', 'store_web')
      updatePixelUser(currentUser, store.miioUser)
      if (isAnonymous) {
        await updateUserLanguage(app.$i18n.locale.value)
      } else {
        if (currentUser.metadata?.creationTime) {
          oneSignalService.addTag('creation_date', currentUser.metadata.creationTime)
        }
        if (currentUser.email) {
          oneSignalService.addEmail(currentUser.email)
        }
        if (currentUser?.phoneNumber) {
          oneSignalService.addTag('phone_number', currentUser?.phoneNumber)
        }
        await getRegisteredUserDetails()
        Promise.all([getDefaultVehicle(), getShippingAddressList(), getUserVat(), getPaymentMethods(), getChargingPreferences()])
      }
      getBasketContent()
    } else {
      clearAuthStore()
      userCookie.value = null
      refreshToken.value = null
      Sentry.setUser(null)
      oneSignalService.logout()
    }
    store.loading = false
    useLoading = true
  }
}

export async function getRegisteredUserDetails(): Promise<void> {
  const store = useStore()
  const router = useRouter()
  const { currentUser } = getAuth()
  const app = useNuxtApp()
  const needEmailConfirmation = confirmEmailAddress()

  if (needEmailConfirmation) {
    await showEmailConfirmation()
    return
  }

  await getUserDetails()

  if (store.miioUser) {
    if (store.miioUser?.default_shipping_address) {
      const address = store.miioUser.default_shipping_address
      if (address?.postal_code) {
        oneSignalService.addTag('postcode', address.postal_code)
      }
      if (address?.location) {
        oneSignalService.addTag('location', address.location)
      }
    }
    if (store.miioUser?.name) {
      const firstName = store.miioUser.name.split(' ')[0]
      const lastName = store.miioUser.name.split(' ').slice(-1).join(' ')
      oneSignalService.addTag('first_name', firstName)
      if (firstName !== lastName) {
        oneSignalService.addTag('last_name', lastName)
      }
    }
    if (store.miioUser?.country_of_origin) {
      oneSignalService.addTag('country_of_origin', store.miioUser.country_of_origin)
    }
  }

  if (app.$i18n.locale.value !== store.miioUser?.language) {
    await updateUserLanguage(store.miioUser?.language != null && store.miioUser?.language !== '' ? store.miioUser.language : app.$i18n.locale.value)
  }

  store.requireLoginValidation = {
    phoneNumber: !currentUser!.phoneNumber,
    country: store.miioUser ? !store.miioUser.has_set_country_of_origin : false
  }

  const requireLoginValidation = store.requireLoginValidation.phoneNumber || store.requireLoginValidation.country

  if (!requireLoginValidation) {
    const currentRoute = router.currentRoute.value.name?.toString() || ''
    if (!currentRoute.includes('checkout')) {
      if (currentRoute.includes('login') || currentRoute.includes('register') || currentRoute.includes('update-user')) {
        const checkOnboarding = checkUserOnBoarding()
        const route = useRoute()
        if (route.query && route.query.redirect) {
          const redirect: string = route.query.redirect.toString()
          changeRoute(redirect === 'account' ? 'account-profile' : redirect)
        } else if (checkOnboarding) {
          changeRoute('onboarding')
        } else {
          changeRoute('index')
        }
      }
    }
  }
}

export function checkUserOnBoarding(): boolean {
  const store = useStore()
  return store.miioUser?.user_on_boarding_store === false
}

function confirmEmailAddress(): boolean {
  const store = useStore()
  if (store.validateEmail) return false
  const auth = getAuth()
  const { currentUser } = auth
  if (currentUser == null) return false
  const userHasFacebook = currentUser.providerData.some((p) => p.providerId === 'facebook.com')
  if (currentUser.emailVerified || userHasFacebook) {
    return false
  } else if (currentUser.email !== null && currentUser.email.length > 0) {
    return true
  }
  return false
}

export async function createAnonymousUser(): Promise<void> {
  useLoading = false
  const auth = getAuth()
  await signInAnonymously(auth)
}

async function deleteCurrentUser(): Promise<void> {
  const auth = getAuth()
  const store = useStore()
  runStateChange = false
  if (auth.currentUser != null && auth.currentUser.isAnonymous) {
    await auth.currentUser.delete()
  }
  runStateChange = true
  store.firebaseUser = auth.currentUser
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  const auth = getAuth()
  try {
    const { currentUser } = auth
    let anonymousUID = null
    if (currentUser && currentUser.isAnonymous) {
      anonymousUID = currentUser.uid
      await deleteCurrentUser()
    }
    await signInWithEmailAndPassword(auth, email, password)
    trackEvent({ event: 'login' })
    if (anonymousUID) {
      await mergeAnonymousBasket(anonymousUID)
    }
  } catch (e) {
    getFirebaseError(e)
  }
}

export async function signInOrAssociatePhoneCodeRequest(phoneNumber: string, appVerifier: RecaptchaVerifier, isOnLogin: boolean): Promise<ConfirmationResult | void> {
  const auth = getAuth()
  try {
    let confirmationResult = null
    if (auth.currentUser && (!isOnLogin || (isOnLogin && auth.currentUser.isAnonymous))) {
      confirmationResult = await linkWithPhoneNumber(auth.currentUser, phoneNumber, appVerifier!)
    } else {
      confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier!)
    }
    return confirmationResult
  } catch (e) {
    if (isOnLogin && e.code && (e.code === 'auth/account-exists-with-different-credential' || e.code === 'auth/credential-already-in-use')) {
      const credential = PhoneAuthProvider.credentialFromError(e)
      await deleteAnonymousAndSignInWithCredential(credential!)
    } else {
      getFirebaseError(e)
    }
  }
}

export async function verifyPhoneCode(code: string, confirmationResult: ConfirmationResult, type: FormTabEnum): Promise<boolean> {
  const auth = getAuth()
  const credential: PhoneAuthCredential = await PhoneAuthProvider.credential(confirmationResult.verificationId, code)
  if (type === FormTabEnum.InfoValidation) {
    try {
      await updatePhoneNumber(auth.currentUser, credential)
      await getRegisteredUserDetails()
    } catch (e) {
      getFirebaseError(e, true)
      return false
    }
  } else {
    try {
      let wasAnonymousUser = false
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        wasAnonymousUser = true
        await linkWithCredential(auth.currentUser, credential)
      } else {
        await signInWithCredential(auth, credential)
      }
      sendLoginOrSignUpEvent(wasAnonymousUser)
    } catch (e) {
      if (e.code && (e.code === 'auth/account-exists-with-different-credential' || e.code === 'auth/credential-already-in-use')) {
        const credential = PhoneAuthProvider.credentialFromError(e)
        await deleteAnonymousAndSignInWithCredential(credential!)
      } else {
        getFirebaseError(e)
        return false
      }
    }
  }
  return true
}

export async function loginWithPopup(providerNumber: number): Promise<void> {
  const auth = getAuth()
  let provider = null
  if (providerNumber === 1) {
    provider = new FacebookAuthProvider()
  } else if (providerNumber === 2) {
    provider = new GoogleAuthProvider()
  } else if (providerNumber === 3) {
    provider = new OAuthProvider('apple.com')
  }
  try {
    let wasAnonymousUser = false
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      wasAnonymousUser = true
      await linkWithPopup(auth.currentUser, provider!)
    } else {
      await signInWithPopup(auth, provider!)
    }
    sendLoginOrSignUpEvent(wasAnonymousUser)
  } catch (e) {
    if (e.code === 'auth/credential-already-in-use' || e.code === 'auth/email-already-in-use') {
      let credential = null
      switch (providerNumber) {
        case 1:
          credential = FacebookAuthProvider.credentialFromError(e)
          break
        case 2:
          credential = GoogleAuthProvider.credentialFromError(e)
          break
        case 3:
          credential = OAuthProvider.credentialFromError(e)
          break
      }
      return await deleteAnonymousAndSignInWithCredential(credential!)
    } else {
      getFirebaseError(e)
    }
  }
}

async function deleteAnonymousAndSignInWithCredential(credential: AuthCredential): Promise<void> {
  const auth = getAuth()
  const { currentUser } = auth
  let anonymousUID = null
  if (currentUser && currentUser.isAnonymous) {
    anonymousUID = currentUser.uid
    await deleteCurrentUser()
  }

  try {
    await signInWithCredential(auth, credential)
    trackEvent({ event: 'login' })
    if (anonymousUID) {
      await mergeAnonymousBasket(anonymousUID)
    }
  } catch (e) {
    getFirebaseError(e)
  }
}

async function showEmailConfirmation(): Promise<void> {
  const auth = getAuth()
  const store = useStore()
  store.validateEmail = true
  await sendEmailVerification(auth.currentUser!)
}

export async function createUserAccount(name: string, email: string, password: string): Promise<void> {
  const auth = getAuth()
  const { currentUser } = auth
  if (currentUser != null && currentUser.isAnonymous) {
    const credential = EmailAuthProvider.credential(email, password)
    try {
      await linkWithCredential(currentUser, credential)
      trackEvent({ event: 'sign_up' })
    } catch (e) {
      getFirebaseError(e)
    }
  } else {
    try {
      await createUserWithEmailAndPassword(auth, email, password)
      trackEvent({ event: 'sign_up' })
    } catch (e) {
      getFirebaseError(e)
    }
  }
  await changeUserDetails({ name })
}

export async function recoverUserPassword(email: string) {
  const auth = getAuth()
  try {
    await sendPasswordResetEmail(auth, email)
  } catch (e) {
    getFirebaseError(e)
  }
}

export function getFirebaseError(error: { code?: string; message?: string }, phoneValidation?: boolean): void {
  const app = useNuxtApp()
  const store = useStore()
  store.loading = false

  if (error.code && error.code.includes('auth/')) {
    error.code = error.code.replace('auth/', '')
  }

  let title = app.$i18n.t('somethingWentWrong')
  let description = app.$i18n.t('tryAgainLater')

  switch (error.code) {
    case 'wrong-password':
      title = app.$i18n.t('incorrectPassword')
      description = app.$i18n.t('enterEmailAndPasswordCorrectly')
      break
    case 'user-not-found':
      title = app.$i18n.t('accountDoesNotExist')
      description = app.$i18n.t('emaildDoesNotExist')
      break
    case 'email-already-in-use':
      title = app.$i18n.t('alreadyExistingEmail')
      description = app.$i18n.t('alreadyRegisteredEmail')
      break
    case 'network_error':
    case 'network-request-failed':
    case 'api-not-available':
      title = app.$i18n.t('networkError')
      description = app.$i18n.t('makeSureConnectedToInternet')
      break
    case 'account-exists-with-different-credential':
      title = app.$i18n.t('accountAlreadyExists')
      description = phoneValidation ? app.$i18n.t('phoneNumberAlreadyInUseByAnotherAccount') : app.$i18n.t('accountAlreadyExistsLoginThere')
      break
    case 'invalid-verification-code':
      title = app.$i18n.t('invalidCode')
      description = app.$i18n.t('codeNotValidatedTryAgain')
      break
    case 'requires-recent-login':
      title = app.$i18n.t('loginAgain')
      description = app.$i18n.t('thisActionRequiresARecentLogin')
      break
    case 'popup-closed-by-user':
    case 'cancelled-popup-request':
      return
    case 'credential-already-in-use':
      title = app.$i18n.t('accountAlreadyExists')
      description = phoneValidation ? app.$i18n.t('phoneNumberAlreadyInUseByAnotherAccount') : app.$i18n.t('accessCredentialsAlreadyInUseByAnotherAccount')
      break
    case 'session-expired':
    case 'code-expired':
      title = app.$i18n.t('smsCodeExpired')
      description = app.$i18n.t('smsCodeExpiredTryAgain')
      break
    case 'too-many-requests':
      title = app.$i18n.t('tooManyRequests')
      description = app.$i18n.t('tooManyRequestsTryAgainLater')
      break
    case 'invalid-phone-number':
      title = app.$i18n.t('invalidPhoneNumber')
      description = app.$i18n.t('invalidPhoneNumberTryAgain')
      break
    case 'operation-not-allowed':
      if (phoneValidation) {
        title = app.$i18n.t('unsupportedNumber')
        description = app.$i18n.t('mobileNumberCountryNotSupported')
      } else {
        Sentry.captureException(error)
      }
      break
    default:
      title = app.$i18n.t('somethingWentWrong')
      description = app.$i18n.t('tryAgainLater')
      Sentry.captureException(error)
  }
  store.error = { title, description }
}

function clearAuthStore(): void {
  try {
    const store = useStore()
    const authToken = useCookie('authToken')
    authToken.value = null
    store.error = null
    store.firebaseUser = null
    store.miioUser = null
    store.validateEmail = false
    store.defaultVehicle = null
    store.shippingAddressList = null
    store.vats = null
    store.requireLoginValidation.phoneNumber = false
    store.requireLoginValidation.country = false
    store.recentProducts = []
    store.recentSearch = []
    store.compareProducts = []
    store.basket = null
    store.isUpdatingBasket = false
    store.rewards = null
    store.wallets = null
  } catch (error) {
    Sentry.captureException(error)
  }
}

export async function signOutUser(reload: boolean): Promise<void> {
  try {
    const route = useRoute()
    const store = useStore()
    store.loading = true
    trackEvent({ event: 'sign_out' })
    await getAuth().signOut()
    if (reload) {
      reloadNuxtApp({ path: '/' })
    } else if (route.query?.redirect) {
      changeRoute('login', { redirect: 'checkout' })
    } else {
      changeRoute('login')
    }
  } catch (e) {
    throwError(e)
  }
}

function sendLoginOrSignUpEvent(wasAnonymousUser: boolean): void {
  try {
    const auth = getAuth()
    if (!auth.currentUser || !auth.currentUser.metadata.creationTime) return
    const creationTime = new Date(auth.currentUser.metadata.creationTime).getTime()
    if (wasAnonymousUser || Date.now() - creationTime < 10000) {
      trackEvent({ event: 'sign_up' })
    } else {
      trackEvent({ event: 'login' })
    }
  } catch (e) {
    Sentry.captureException(e)
  }
}

function updatePixelUser(currentFirebaseUser: User, currentMiioUser: MiioUser | null) {
  const config = useRuntimeConfig()
  if (config.public.isDev) return
  try {
    const app = useNuxtApp()
    if (!currentFirebaseUser.isAnonymous) {
      let nameParts = currentFirebaseUser.displayName?.split(' ')
      nameParts = nameParts ?? currentMiioUser?.name?.split(' ')

      const city = currentMiioUser?.default_shipping_address?.location?.replace(/\s+/g, '').toLowerCase()
      const zip = currentMiioUser?.default_shipping_address?.postal_code
      const country = currentMiioUser?.default_shipping_address?.country?.toLowerCase() ?? app.$i18n.locale.value.toLowerCase()

      const firstName = nameParts ? nameParts[0] : null
      const lastName = nameParts && nameParts.length > 1 ? nameParts.slice(1).join(' ') : null

      app.$fb.setUserData({
        external_id: currentFirebaseUser.uid,
        em: currentFirebaseUser.email,
        ph: currentFirebaseUser.phoneNumber,
        fn: firstName,
        ln: lastName,
        ct: city,
        zp: zip,
        country
      })
    } else {
      app.$fb.setUserData({
        external_id: currentFirebaseUser.uid,
        em: null,
        ph: null,
        fn: null,
        ln: null,
        ct: null,
        zp: null,
        country: app.$i18n.locale.value.toLowerCase()
      })
    }
  } catch (e) {
    Sentry.captureException(e)
  }
}
